import InputDate from "components/atoms/InputDate";
import InputRadio from "components/atoms/InputRadio";
import InputSelect from "components/atoms/InputSelect";
import { BreadcrumbNav } from "components/molecules";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp } from "react-feather";
import { useSelector } from "react-redux";
import { MonthOptions, UserLevel, Source } from "utils/constant";
import Button from "components/atoms/Button";
import { HashLoader } from "react-spinners";
import TableComponent from "./Table";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import LaporanBKK from "services/LaporanBKK";
import BKK from "services/BKK";
import Wilayah from "services/Wilayah";

const LaporanBKKService = new LaporanBKK();
const WilayahService = new Wilayah();
const BKKService = new BKK();

const DetailLaporan = () => {
  const module = "Mikroskopis BKK";

  const { currentUser } = useSelector((state) => state.authReducer);
  const tableRef = useRef(null);

  const history = useHistory();

  const [reportData, setReportData] = useState([]);
  const [showFilter, setShowFilter] = useState(true);
  const [showData, setShowData] = useState(false);
  const [levelData, setLevelData] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(currentUser.level);
  const [applySelectedLevel, setApplySelectedLevel] = useState(
    currentUser.level,
  );
  const [currentLevel, setCurrentLevel] = useState("");
  const [levelIdValue, setLevelIdValue] = useState(
    currentUser.username + "|" + currentUser.first_name,
  );
  const [levelDataOptions, setLevelDataOptions] = useState({
    provinces: [],
    districts: [],
    induks: [],
    wilkers: [],
  });
  const [selectedProvince, setSelectedProvince] = useState(
    currentUser?.province_id,
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    currentUser?.district_id,
  );
  const [selectedInduk, setSelectedInduk] = useState("");
  const [selectedWilker, setSelectedWilker] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  // use as report information on table
  const [reportInfo, setReportInfo] = useState({});

  const fetchAllProvincies = async () => {
    try {
      const response = await WilayahService.getAll(Source.PROVINCE, {
        page: 1,
        limit: 9999,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        provinces: response.province.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDistrictByProvince = async (provinceId) => {
    try {
      const response = await WilayahService.getAll(Source.DISTRICT, {
        page: 1,
        limit: 9999,
        searchBy: "province_id",
        searchValue: provinceId,
      });
      setLevelDataOptions((prevState) => ({
        ...prevState,
        districts: response.district.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBKKInduk = async () => {
    try {
      const response = await BKKService.getAll({
        page: 1,
        limit: 9999,
        province_id: selectedProvince?.split("|")[0],
        district_id: selectedDistrict?.split("|")[0],
      });

      const bkkList = response.bkk || [];
      const induks = bkkList.filter((item) => !item.induk_id);

      setLevelDataOptions((prevState) => ({
        ...prevState,
        induks: induks.map((item) => {
          return {
            label: item.name,
            value: `${item.id}|${item.name}`,
          };
        }),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBKKWilker = async (indukId) => {
    try {
      const params = {
        page: 1,
        limit: 9999,
      };

      if (currentUser.level === UserLevel.District) {
        // District user: fetch all wilkers by district_id
        params.district_id = currentUser.district_id;
      } else {
        // Other users: fetch wilkers by indukId
        if (!indukId) return;
        params.searchBy = "induk_id";
        params.searchValue = indukId;
      }

      const response = await BKKService.getAll(params);

      const bkkList = response.bkk || [];
      // For District users, filter to only show wilkers (items with induk_id)
      const wilkers =
        currentUser.level === UserLevel.District
          ? bkkList.filter((item) => item.induk_id)
          : bkkList;

      setLevelDataOptions((prevState) => ({
        ...prevState,
        wilkers: wilkers.map((item) => {
          return {
            label: item.name,
            value: `${item.id}|${item.name}`,
          };
        }),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const generateLevelOptions = (userLevel) => {
    switch (userLevel) {
      case UserLevel.BKKWilker:
        return [{ label: "BKK Wilker", value: "wilker" }];
      case UserLevel.BKKInduk:
        return [
          { label: "BKK Induk", value: "induk" },
          { label: "BKK Wilker", value: "wilker" },
        ];
      case UserLevel.Province:
        return [
          { label: "Provinsi", value: "province" },
          { label: "Kabupaten/Kota", value: "district" },
          { label: "BKK Induk", value: "induk" },
          { label: "BKK Wilker", value: "wilker" },
        ];
      case UserLevel.District:
        return [
          { label: "Kabupaten/Kota", value: "district" },
          { label: "BKK Induk", value: "induk" },
          { label: "BKK Wilker", value: "wilker" },
        ];
      default:
        return [
          { label: "Nasional", value: "nasional" },
          { label: "Provinsi", value: "province" },
          { label: "Kabupaten/Kota", value: "district" },
          { label: "BKK Induk", value: "induk" },
          { label: "BKK Wilker", value: "wilker" },
        ];
    }
  };

  useEffect(() => {
    const initData = async () => {
      // Set Level and level-related state
      const options = generateLevelOptions(currentUser.level);
      setLevelData(options);

      if (currentUser.level === UserLevel.BKKWilker) {
        setSelectedLevel("wilker");
        setLevelIdValue(`${currentUser.username}|${currentUser.first_name}`);
        const getBkk = await BKKService.getIndukByWilker(currentUser.username);
        setSelectedInduk(`${getBkk.induk_id}|${getBkk.induk_name}`);
        setSelectedWilker(`${currentUser.username}|${currentUser.first_name}`);
      } else if (currentUser.level === UserLevel.BKKInduk) {
        setSelectedLevel("induk");
        setLevelIdValue(`${currentUser.username}|${currentUser.first_name}`);
        setSelectedInduk(`${currentUser.username}|${currentUser.first_name}`);
      } else if (currentUser.level === UserLevel.District) {
        setSelectedLevel("district");
        setSelectedProvince(currentUser?.province_id);
        setSelectedDistrict(currentUser?.district_id);
        setLevelIdValue(currentUser?.district_id);
      } else if (currentUser.level === UserLevel.Province) {
        setSelectedLevel("province");
        setSelectedProvince(currentUser?.province_id);
        setLevelIdValue(currentUser?.province_id);
      } else {
        setSelectedLevel("nasional");
        setLevelIdValue("");
      }
    };
    if (currentUser) initData();
  }, [currentUser]);

  useEffect(() => {
    if (selectedLevel === "induk" || selectedLevel === "wilker") {
      fetchBKKInduk();
    }
    // For District users, auto-fetch wilkers when selecting "wilker" level
    if (
      selectedLevel === "wilker" &&
      currentUser.level === UserLevel.District
    ) {
      fetchBKKWilker();
    }
  }, [selectedLevel, selectedProvince, selectedDistrict]);

  useEffect(() => {
    fetchAllProvincies();
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      fetchDistrictByProvince(selectedProvince?.split("|")[0]);
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (
      selectedProvince &&
      !String(selectedProvince).includes("|") &&
      levelDataOptions.provinces.length > 0
    ) {
      const found = levelDataOptions.provinces.find(
        (p) => p.value.split("|")[0] === String(selectedProvince),
      );
      if (found) {
        setSelectedProvince(found.value);
        if (selectedLevel === "province") setLevelIdValue(found.value);
      }
    }
  }, [levelDataOptions.provinces, selectedProvince]);

  useEffect(() => {
    if (
      selectedDistrict &&
      !String(selectedDistrict).includes("|") &&
      levelDataOptions.districts.length > 0
    ) {
      const found = levelDataOptions.districts.find(
        (d) => d.value.split("|")[0] === String(selectedDistrict),
      );
      if (found) {
        setSelectedDistrict(found.value);
        if (selectedLevel === "district") setLevelIdValue(found.value);
      }
    }
  }, [levelDataOptions.districts, selectedDistrict]);

  useEffect(() => {
    if (selectedInduk) {
      fetchBKKWilker(selectedInduk.split("|")[0]);
    }
  }, [selectedInduk]);

  const handleApplyFilter = async () => {
    setApplySelectedLevel(selectedLevel);
    setIsLoading(true);
    setShowData(false);

    const params = {
      level: selectedLevel,
      level_id: levelIdValue,
    };

    try {
      const response = await LaporanBKKService.MicroscopistBKK(params);
      setReportInfo({
        level: selectedLevel,
        induk: selectedInduk,
        wilker: selectedWilker,
      });
      setReportData(response);
      setCurrentLevel(selectedLevel);
    } catch (error) {
      console.log(error);
      setReportData([]);
    } finally {
      setIsLoading(false);
      setShowData(true);
    }
  };

  const exportToExcel = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "Sheet 1" });
    const levelName =
      selectedLevel === "nasional"
        ? "Nasional"
        : selectedLevel === "induk"
          ? selectedInduk?.split("|")[1]
          : selectedWilker?.split("|")[1];
    XLSX.writeFile(
      wb,
      `LAPORAN_MIKROSKOPIS_BKK_${levelName}_${moment().format("DDMMYYYY")}.xlsx`,
    );
  };

  return (
    <div className="dashboard-wrapper">
      <BreadcrumbNav
        items={[
          { label: "Laporan", onClick: () => history.push("/laporan") },
          { label: module },
        ]}
      />
      <div className="filter">
        <div className="filter-header">
          <p>FILTER</p>
          <button
            className="btn_toggle_filter"
            onClick={() => setShowFilter(!showFilter)}
          >
            {showFilter ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
          </button>
        </div>
        {showFilter && (
          <div className="filter-body">
            <div className="filter-item">
              <div className="filter-label">
                <p>Level</p>
              </div>
              <div className="filter-field">
                <InputRadio
                  options={levelData}
                  onChange={(e) => {
                    const level = e.target.value;
                    setSelectedLevel(level);

                    if (currentUser.level === UserLevel.National) {
                      setSelectedProvince("");
                      setSelectedDistrict("");
                      setSelectedInduk("");
                      setSelectedWilker("");
                      setLevelIdValue("");
                    } else if (currentUser.level === UserLevel.Province) {
                      setSelectedDistrict("");
                      setSelectedInduk("");
                      setSelectedWilker("");
                      setLevelIdValue(currentUser.province_id || "");
                    } else if (currentUser.level === UserLevel.District) {
                      setSelectedInduk("");
                      setSelectedWilker("");
                      setLevelIdValue(currentUser.district_id || "");
                    } else if (currentUser.level === UserLevel.BKKInduk) {
                      setSelectedWilker("");
                      setLevelIdValue(
                        `${currentUser.username}|${currentUser.first_name}`,
                      );
                    } else if (currentUser.level === UserLevel.BKKWilker) {
                      setLevelIdValue(
                        `${currentUser.username}|${currentUser.first_name}`,
                      );
                    }
                  }}
                  selectedOption={selectedLevel}
                />
                <div className="advanced-filter">
                  {selectedLevel === "province" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1.2rem",
                      }}
                    >
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih Provinsi"}
                          data={levelDataOptions.provinces}
                          onChange={(e) => {
                            setSelectedProvince(e.value);
                            setLevelIdValue(e.value);
                          }}
                          value={
                            levelDataOptions.provinces?.find(
                              (item) => item.value === selectedProvince,
                            ) || ""
                          }
                          isDisabled={
                            currentUser.level === UserLevel.National
                              ? false
                              : true
                          }
                        />
                      </div>
                    </div>
                  )}
                  {selectedLevel === "district" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1.2rem",
                      }}
                    >
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih Provinsi"}
                          data={levelDataOptions.provinces}
                          onChange={(e) => {
                            setSelectedProvince(e.value);
                            setSelectedDistrict("");
                          }}
                          value={
                            levelDataOptions.provinces?.find(
                              (item) => item.value === selectedProvince,
                            ) || ""
                          }
                          isDisabled={
                            currentUser.level === UserLevel.National
                              ? false
                              : true
                          }
                        />
                      </div>
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih Kab/Kota"}
                          data={
                            currentUser?.level === UserLevel.District
                              ? levelDataOptions.districts?.filter(
                                  (item) =>
                                    item.value === currentUser?.district_id,
                                )
                              : levelDataOptions.districts
                          }
                          onChange={(e) => {
                            setSelectedDistrict(e.value);
                            setLevelIdValue(e.value);
                          }}
                          value={
                            levelDataOptions.districts?.find(
                              (item) => item.value === selectedDistrict,
                            ) || ""
                          }
                          isDisabled={
                            currentUser.level === UserLevel.National ||
                            currentUser.level === UserLevel.Province
                              ? false
                              : true
                          }
                        />
                      </div>
                    </div>
                  )}
                  {selectedLevel === "induk" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1.2rem",
                      }}
                    >
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih BKK Induk"}
                          data={levelDataOptions.induks}
                          onChange={(e) => {
                            setSelectedInduk(e.value);
                            setLevelIdValue(e.value);
                          }}
                          value={
                            levelDataOptions.induks?.find(
                              (item) => item.value === selectedInduk,
                            ) || ""
                          }
                          isDisabled={
                            currentUser?.level === UserLevel.National ||
                            currentUser?.level === UserLevel.Province ||
                            levelDataOptions.induks?.length > 0
                              ? false
                              : true
                          }
                        />
                      </div>
                    </div>
                  )}
                  {selectedLevel === "wilker" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1.2rem",
                      }}
                    >
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih BKK Induk"}
                          data={levelDataOptions.induks}
                          onChange={(e) => {
                            setSelectedInduk(e.value);
                            setSelectedWilker("");
                            setLevelIdValue("");
                          }}
                          value={
                            levelDataOptions.induks?.find(
                              (item) => item.value === selectedInduk,
                            ) || ""
                          }
                          isDisabled={
                            currentUser?.level === UserLevel.National ||
                            currentUser?.level === UserLevel.Province ||
                            levelDataOptions.induks?.length > 0
                              ? false
                              : true
                          }
                        />
                      </div>
                      <div style={{ flex: "1" }}>
                        <InputSelect
                          isClearable={false}
                          placeholder={"Pilih BKK Wilker"}
                          data={
                            currentUser.level === UserLevel.BKKWilker
                              ? levelDataOptions.wilkers.filter(
                                  (i) =>
                                    i.value ===
                                    `${currentUser.username}|${currentUser.first_name}`,
                                )
                              : levelDataOptions.wilkers
                          }
                          onChange={(e) => {
                            let value = e.map(function (item) {
                              return item.value;
                            });
                            let resultValue = value.join(",");
                            setSelectedWilker(resultValue);
                            setLevelIdValue(resultValue);
                          }}
                          value={levelDataOptions.wilkers?.filter((item) =>
                            selectedWilker.includes(item.value),
                          )}
                          isMultipleSelect={true}
                          isDisabled={
                            currentUser?.level === UserLevel.National ||
                            currentUser?.level === UserLevel.Province ||
                            currentUser?.level === UserLevel.District ||
                            currentUser?.level === UserLevel.BKKInduk ||
                            levelDataOptions.wilkers?.length > 0
                              ? false
                              : true
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="filter-item">
              <div className="filter-field">
                <div className="filter-condition">
                  <Button
                    variant={"primary"}
                    size={"normal"}
                    onClick={() => handleApplyFilter()}
                    isDisabled={selectedLevel !== "nasional" && !levelIdValue}
                  >
                    Terapkan
                  </Button>
                  {showData && (
                    <Button
                      variant={"secondary"}
                      size={"normal"}
                      onClick={exportToExcel}
                    >
                      Export Excel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* loading spinner */}
      {isLoading && !showData && (
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            marginTop: "5rem",
          }}
        >
          <HashLoader size={100} color="#18b3ab" />
        </div>
      )}

      {showData && (
        <div className="table-container">
          <div className="data-title">
            <h2>{module}</h2>
          </div>
          <div className="data-main">
            <TableComponent
              currentLevel={applySelectedLevel}
              reportsData={reportData}
              tableRef={tableRef}
              reportInfo={reportInfo}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailLaporan;
